import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  /** Delay before mounting (ms). Prefer idle when available. */
  delayMs?: number;
  fallback?: ReactNode;
};

/**
 * Defer non-critical UI until the browser is idle so first paint stays snappy.
 */
export function DeferredMount({ children, delayMs = 0, fallback = null }: Props) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;
    let idleId: number | undefined;

    const enable = () => {
      if (!cancelled) setReady(true);
    };

    const schedule = () => {
      if (delayMs > 0) {
        timeoutId = window.setTimeout(enable, delayMs);
        return;
      }
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(() => enable(), { timeout: 2000 });
      } else {
        timeoutId = globalThis.setTimeout(enable, 200) as unknown as number;
      }
    };

    schedule();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      if (idleId && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [delayMs]);

  if (!ready) return <>{fallback}</>;
  return <>{children}</>;
}

export function lazyNamed<T extends Record<string, unknown>, K extends keyof T>(
  loader: () => Promise<T>,
  exportName: K,
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[exportName] as React.ComponentType<any> };
  });
}

export { Suspense };
